import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import BirthFields, { birthPayload, emptyBirth } from '../../components/BirthFields/BirthFields';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import { browserSiteLanguage } from '../../data/browserLanguage';
import { chartDirection, chartHref, fill, useChartLanguage } from '../../data/chartLanguage';
import {
  aspectName, bodyName, formatDay, formatWhen, keywordLine, noteText, phaseName,
  resolveDailyLanguage, signName, statusName, useDailyCopy,
} from '../../data/dailyLanguage';
import { SITE_LANGUAGE_LANDINGS } from '../../data/siteLanguages';
import { askChart, drawDaily, drawDailyView } from '../../services/chartService';
import {
  chartSummary, dailyVisitMeta, forgetSavedChart, markDailyVisit, readSavedChart, writeSavedChart,
} from '../../services/savedChart';
import MoonIcon from '../../components/MoonIcon/MoonIcon';
import { wrapReading } from '../BirthChart/ReadingText';
import DailyReading from './DailyReading';
import '../BirthChart/BirthChart.css';
import './DailyHoroscope.css';

const FEATURED_LANGUAGES = 6;
const ENGLISH = { code: 'en', flag: '🌐', nativeLabel: 'English' };
const CARD_OF_THE_DAY_PATHS = {
  es: '/es/carta-del-dia/',
  ar: '/ar/card-of-the-day/',
};

function TransitDialog({ row, copy, language, onClose }) {
  const closeRef = useRef(null);
  useEffect(() => {
    closeRef.current?.focus();
  }, []);
  const explain = fill(copy.modalExplain || '{transit} is traditionally associated with {transitKeyword}. {natal} relates to {natalKeyword}. This connection is often read as {aspectKeyword}.', {
    transit: bodyName(copy, row.transit),
    natal: bodyName(copy, row.natal_label),
    transitKeyword: copy.planetKeywords?.[row.transit] || '',
    natalKeyword: copy.planetKeywords?.[row.natal] || copy.planetKeywords?.[row.natal_label] || '',
    aspectKeyword: copy.aspectKeywords?.[row.aspect] || '',
  });
  return (
    <div className="daily-modal" role="presentation" onClick={onClose}>
      <div
        className="daily-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="daily-tech-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="daily-tech-title">{copy.modalTitle || 'Astrological details'}</h2>
        <dl>
          <div>
            <dt>{copy.modalTransit || 'Transit'}</dt>
            <dd>{fill(copy.transitLine, {
              transit: bodyName(copy, row.transit),
              aspect: aspectName(copy, row.aspect),
              natal: bodyName(copy, row.natal_label),
            })}</dd>
          </div>
          <div>
            <dt>{copy.modalAspect || 'Aspect'}</dt>
            <dd>{aspectName(copy, row.aspect)}</dd>
          </div>
          <div>
            <dt>{copy.modalOrb || 'Orb'}</dt>
            <dd>{row.orb}</dd>
          </div>
          <div>
            <dt>{copy.modalStatus || 'Status'}</dt>
            <dd>{statusName(copy, row.status_label)}</dd>
          </div>
          {row.exact?.length > 0 && (
            <div>
              <dt>{copy.modalExact || 'Exact time'}</dt>
              <dd>{row.exact.map((item) => formatWhen(item.at, language)).join(' · ')}</dd>
            </div>
          )}
          {row.house ? (
            <div>
              <dt>{copy.modalHouse || 'House'}</dt>
              <dd>{row.house}</dd>
            </div>
          ) : null}
        </dl>
        {explain && (
          <>
            <h3>{copy.modalMeaning || 'What this traditionally means'}</h3>
            <p>{explain}</p>
          </>
        )}
        <button ref={closeRef} type="button" className="birth-draw" onClick={onClose}>{copy.modalClose || 'Close'}</button>
      </div>
    </div>
  );
}

function longDate(isoDate, view, language) {
  const base = isoDate ? new Date(`${isoDate}T12:00:00`) : new Date();
  if (Number.isNaN(base.getTime())) return isoDate || '';
  const shift = (days) => new Date(base.getTime() + days * 86400000);
  const format = (date, options) => new Intl.DateTimeFormat(language || 'en', options).format(date);
  if (view === 'week') {
    const options = { day: 'numeric', month: 'long' };
    return `${format(base, options)} – ${format(shift(6), options)}`;
  }
  return format(view === 'tomorrow' ? shift(1) : base, { weekday: 'long', day: 'numeric', month: 'long' });
}

function viewerZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function formFromBirth(birth) {
  let hour = '';
  let ampm = 'AM';
  if (!birth.time_unknown && birth.hour != null) {
    ampm = birth.hour >= 12 ? 'PM' : 'AM';
    hour = String(birth.hour % 12 || 12);
  }
  return {
    ...emptyBirth(birth.name || ''),
    month: String(birth.month || ''),
    day: String(birth.day || ''),
    year: String(birth.year || ''),
    hour,
    minute: birth.minute == null || birth.time_unknown ? '' : String(birth.minute),
    ampm,
    timeUnknown: Boolean(birth.time_unknown),
    placeQuery: birth.place_label || '',
    place: birth.place_label ? {
      label: birth.place_label,
      latitude: birth.latitude,
      longitude: birth.longitude,
      timezone: birth.timezone,
    } : null,
  };
}

function featuredLanguages(current, leadCode) {
  const all = [ENGLISH, ...SITE_LANGUAGE_LANDINGS];
  const prefer = current !== 'en' ? current : leadCode;
  const match = prefer ? all.find((language) => language.code === prefer) : null;
  const ordered = match ? [match, ...all.filter((language) => language.code !== match.code)] : all;
  return { all, featured: ordered.slice(0, FEATURED_LANGUAGES) };
}

function LanguageChoices({ language, onChoose, allLabel }) {
  const [leadCode, setLeadCode] = useState(null);
  useLayoutEffect(() => {
    const code = browserSiteLanguage();
    setLeadCode(code !== 'en' ? code : null);
  }, []);
  const { all, featured } = featuredLanguages(language, leadCode);
  const button = (item) => (
    <button
      key={item.code}
      type="button"
      lang={item.code}
      className={item.code === language ? 'active' : ''}
      aria-pressed={item.code === language}
      onClick={() => onChoose(item.code)}
    >
      <span aria-hidden="true">{item.flag}</span> {item.nativeLabel}
    </button>
  );
  return (
    <nav className="daily-langs" aria-label="Language">
      {featured.map(button)}
      <details className="daily-langs-more">
        <summary>{allLabel}</summary>
        <div className="daily-langs-grid">
          {all.map(button)}
        </div>
      </details>
    </nav>
  );
}

function PrivacyNote({ template, label }) {
  const [before, after = ''] = String(template || '').split('{privacy}');
  return (
    <p className="birth-note">
      {before}
      <Link to="/privacy/">{label}</Link>
      {after}
    </p>
  );
}

const DailyHoroscope = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, chooseLanguage } = useChartLanguage();
  const copy = useDailyCopy(language);
  const [form, setForm] = useState(emptyBirth);
  const [phase, setPhase] = useState('form');
  const [bundle, setBundle] = useState(null);
  const [view, setView] = useState('today');
  const [readings, setReadings] = useState({});
  const [readingError, setReadingError] = useState('');
  const [error, setError] = useState('');
  const [session, setSession] = useState(null);
  const [birth, setBirth] = useState(null);
  const [question, setQuestion] = useState('');
  const [answers, setAnswers] = useState([]);
  const [asking, setAsking] = useState(false);
  const [viewBusy, setViewBusy] = useState(false);
  const [techRow, setTechRow] = useState(null);
  const abortRef = useRef(null);
  const askAbortRef = useRef(null);
  const startRef = useRef(null);
  const booted = useRef(false);
  const ranLang = useRef(null);

  const run = (payloadBirth, { birthSession = null, saved = true, countVisit = true, lang = language } = {}) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    startRef.current = { payloadBirth, birthSession };
    setError('');
    setReadingError('');
    setReadings({});
    setAnswers([]);
    setSession(null);
    setView('today');
    setPhase('loading');
    setBirth(payloadBirth);
    let got = false;
    drawDaily({
      birth: birthSession ? null : payloadBirth,
      birth_session: birthSession,
      viewer_timezone: viewerZone(),
      view: 'today',
      language: lang,
      reading: true,
      saved,
      ...(countVisit ? dailyVisitMeta() : {}),
    }, {
      signal: controller.signal,
      onTransits: (data) => {
        got = true;
        setBundle(data);
        setPhase('ready');
        if (countVisit) markDailyVisit();
      },
      onToken: (token) => setReadings((current) => ({ ...current, today: (current.today || '') + token })),
      onDone: (data) => {
        setPhase(got ? 'ready' : 'form');
        if (data?.session) setSession(data.session);
        if (!got) setError(copy.chartFailed);
      },
      onError: (message) => {
        if (got) setReadingError(message);
        else {
          setPhase('form');
          setError(message);
        }
      },
    });
  };

  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') return undefined;
    const resolved = resolveDailyLanguage(location.search);
    if (!booted.current) {
      booted.current = true;
      if (language !== resolved) return undefined;
    }
    if (ranLang.current === language) return undefined;
    const first = ranLang.current == null;
    ranLang.current = language;
    if (!first) {
      const start = startRef.current;
      if (!start) return undefined;
      run(start.payloadBirth, {
        birthSession: start.birthSession,
        saved: false,
        countVisit: false,
        lang: language,
      });
      return () => abortRef.current?.abort();
    }
    const saved = readSavedChart();
    const chart = new URLSearchParams(location.search).get('chart');
    if (saved) {
      setForm(formFromBirth(saved));
      run(saved, { saved: true, lang: language });
    } else if (chart) {
      run(null, { birthSession: chart, saved: false, lang: language });
    }
    return () => {
      abortRef.current?.abort();
      askAbortRef.current?.abort();
    };
    // The reading starts once the language from the link or cookie is known,
    // and again when the visitor picks a different language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const pickLanguage = (code) => {
    chooseLanguage(code);
    const params = new URLSearchParams(location.search);
    if (code === 'en') params.delete('lang');
    else params.set('lang', code);
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const payload = birthPayload(form, { requirePlace: true, requireTime: true, messages: copy.messages });
    if (payload.error) {
      setError(payload.error[0].toUpperCase() + payload.error.slice(1));
      return;
    }
    writeSavedChart(payload);
    setForm(formFromBirth(payload));
    ranLang.current = language;
    run(payload, { saved: true, lang: language });
  };

  const loadView = (next) => {
    setView(next);
    if (next === 'today' || readings[next] || !session || viewBusy) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setViewBusy(true);
    setReadingError('');
    drawDailyView(session, next, {
      signal: controller.signal,
      onToken: (token) => setReadings((current) => ({ ...current, [next]: (current[next] || '') + token })),
      onDone: () => setViewBusy(false),
      onError: (message) => {
        setReadingError(message);
        setViewBusy(false);
      },
    });
  };

  const handleAsk = (event, preset) => {
    event?.preventDefault();
    const text = (preset || question).trim();
    if (!session || asking || !text) return;
    const controller = new AbortController();
    askAbortRef.current = controller;
    const index = answers.length;
    setAnswers((current) => [...current, { question: text, answer: '', error: '' }]);
    setQuestion('');
    setAsking(true);
    askChart(session, text, {
      signal: controller.signal,
      onToken: (token) => setAnswers((current) => current.map((item, itemIndex) => (
        itemIndex === index ? { ...item, answer: item.answer + token } : item
      ))),
      onDone: () => setAsking(false),
      onError: (message) => {
        setAnswers((current) => current.map((item, itemIndex) => (
          itemIndex === index ? { ...item, error: message } : item
        )));
        setAsking(false);
      },
    });
  };

  const forget = () => {
    abortRef.current?.abort();
    forgetSavedChart();
    startRef.current = null;
    setBundle(null);
    setBirth(null);
    setSession(null);
    setReadings({});
    setForm(emptyBirth());
    setPhase('form');
  };

  const current = bundle?.views?.[view];
  const shown = phase !== 'form';
  const direction = chartDirection(language);

  useEffect(() => {
    if (!techRow) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setTechRow(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [techRow]);
  const when = (iso) => formatWhen(iso, language);

  return (
    <div className="birth-page">
      <Helmet>
        <html lang={language} />
        <title>{copy.seoTitle}</title>
        <meta name="description" content={copy.seoDescription} />
        <link rel="canonical" href="https://nowtarot.com/personalized-daily-horoscope/" />
        <meta property="og:title" content={copy.ogTitle} />
        <meta property="og:description" content={copy.ogDescription} />
        <meta property="og:url" content="https://nowtarot.com/personalized-daily-horoscope/" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'WebApplication',
                name: copy.appName,
                url: 'https://nowtarot.com/personalized-daily-horoscope/',
                applicationCategory: 'LifestyleApplication',
                offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
                description: copy.appDescription,
              },
              {
                '@type': 'FAQPage',
                inLanguage: language,
                mainEntity: (copy.faq || []).map((item) => ({
                  '@type': 'Question',
                  name: item.q,
                  acceptedAnswer: { '@type': 'Answer', text: item.a },
                })),
              },
            ],
          })}
        </script>
      </Helmet>
      <Header />
      <main className={shown ? 'birth-wide' : ''} {...direction}>
        <div className="daily-lang-bar">
          <span className="daily-langs-label">{copy.langsLabel}</span>
          <LanguageChoices language={language} onChoose={pickLanguage} allLabel={fill(copy.langsAll, { n: SITE_LANGUAGE_LANDINGS.length + 1 })} />
        </div>
        {shown ? (
          <div className="birth-result daily-result">
            <header className="daily-hero">
              <div className="daily-hero-text">
                <p className="birth-eyebrow">{copy.headingBefore}</p>
                <h1>{longDate(bundle?.viewer_date, view, language)}</h1>
                <p className="daily-hero-chart">
                  {chartSummary(birth) || copy.yourChart}
                  <span className="daily-summary-actions">
                    <button type="button" className="birth-text" onClick={() => setPhase('form')}>{copy.edit}</button>
                    <button type="button" className="birth-text" onClick={forget}>{copy.forget}</button>
                  </span>
                </p>
              </div>
              {current?.sky && (
                <div className="daily-hero-sky">
                  <MoonIcon angle={current.sky.angle ?? 135} size={64} />
                  <span>
                    <strong>{phaseName(copy, current.sky)}</strong>
                    <small>{bodyName(copy, 'Moon')} · {signName(copy, current.sky.moon_sign)}</small>
                    <small>{bodyName(copy, 'Sun')} · {signName(copy, current.sky.sun_sign)}</small>
                  </span>
                </div>
              )}
            </header>
            {bundle?.notes?.map((note) => <p className="birth-note" key={note}>{noteText(copy, note)}</p>)}
            <div className="daily-tabs" role="tablist" aria-label={copy.tabsLabel}>
              {['today', 'tomorrow', 'week'].map((id) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={view === id}
                  className={view === id ? 'active' : ''}
                  disabled={(id !== 'today' && !session) || viewBusy}
                  onClick={() => loadView(id)}
                >
                  {copy.tabs[id]}
                </button>
              ))}
            </div>
            {!bundle && <p className="birth-wait">{copy.calculating}</p>}
            <section aria-label={copy.readingTitle}>
              <DailyReading
                text={readings[view]}
                view={view}
                copy={copy}
                language={language}
                transits={current?.transits}
                onDetails={setTechRow}
              />
              {!readings[view] && !readingError && <p className="birth-wait daily-writing">{copy.writing}</p>}
              {readingError && <p className="birth-error">{readingError}</p>}
            </section>
            {current?.sky && (
              <details className="daily-advanced">
                <summary>{copy.skyToggle || "What's happening in the sky?"}</summary>
                <p>
                  {fill(copy.skyLine, {
                    sign: signName(copy, current.sky.sun_sign),
                    moon: signName(copy, current.sky.moon_sign),
                    degree: current.sky.moon_degree,
                    phase: phaseName(copy, current.sky),
                  })}
                  {current.sky.moon_ingress ? ` ${fill(copy.moonEnters, {
                    sign: signName(copy, current.sky.moon_ingress.sign),
                    when: when(current.sky.moon_ingress.at),
                  })}` : ''}
                  {current.sky.retrograde?.length
                    ? ` ${fill(copy.retrograde, { planets: current.sky.retrograde.map((name) => bodyName(copy, name)).join(', ') })}`
                    : ` ${copy.noRetrograde}`}
                </p>
              </details>
            )}
            {current && (
              <details className="daily-advanced">
                <summary>{copy.viewAll || 'View all planetary transits'}</summary>
                {current.transits.length === 0 ? (
                  <p className="birth-note">{copy.quiet}</p>
                ) : (
                  <ul className="daily-list">
                    {current.transits.map((row) => (
                      <li key={`${row.transit}-${row.aspect}-${row.natal}`}>
                        <div className="daily-list-head">
                          <strong>{fill(copy.transitLine, {
                            transit: bodyName(copy, row.transit),
                            aspect: aspectName(copy, row.aspect),
                            natal: bodyName(copy, row.natal_label),
                          })}</strong>
                          <span className={`daily-chip daily-chip-${row.status}`}>{statusName(copy, row.status_label)}</span>
                        </div>
                        <p className="daily-keywords">{keywordLine(copy, row)}</p>
                        <button type="button" className="birth-text" onClick={() => setTechRow(row)}>
                          {copy.techDetails || 'View technical details'}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <section className="daily-themes" aria-label={copy.themesLabel}>
                  {['relationships', 'career', 'growth'].map((key) => (
                    <article key={key}>
                      <h3>{copy.themes[key]}</h3>
                      {(current.themes?.[key] || []).length === 0 ? (
                        <p>{copy.themeQuiet}</p>
                      ) : (
                        <ul>
                          {current.themes[key].map((item) => (
                            <li key={`${item.transit}-${item.aspect}-${item.natal}`}>
                              {item.aspect === 'ingress'
                                ? fill(copy.moonEntersTheme, { sign: signName(copy, item.natal) })
                                : `${bodyName(copy, item.transit)} ${aspectName(copy, item.aspect)} ${bodyName(copy, item.natal)}`}
                              <small>{statusName(copy, item.status_label)}</small>
                            </li>
                          ))}
                        </ul>
                      )}
                    </article>
                  ))}
                </section>
                {view === 'week' && current.timeline?.length > 0 && (
                  <section>
                    <h3>{copy.weekTitle}</h3>
                    <ol className="daily-timeline">
                      {current.timeline.map((day) => (
                        <li key={day.date}>
                          <strong>{formatDay(day.date, language)}</strong>
                          <ul>
                            {day.events.slice(0, 8).map((event, index) => (
                              <li key={`${event.at}-${event.kind}-${index}`}>
                                {event.kind === 'aspect' && fill(copy.aspectEvent, {
                                  transit: bodyName(copy, event.transit),
                                  aspect: aspectName(copy, event.aspect),
                                  natal: bodyName(copy, event.natal),
                                })}
                                {event.kind === 'ingress' && fill(copy.enters, {
                                  body: bodyName(copy, event.body),
                                  sign: signName(copy, event.sign),
                                })}
                                {event.kind === 'station' && fill(copy.stations, {
                                  body: bodyName(copy, event.body),
                                  motion: copy.motion?.[event.motion] || event.motion,
                                })}
                                {event.kind === 'house' && fill(copy.entersHouse, {
                                  body: bodyName(copy, event.body),
                                  house: event.house,
                                })}
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ol>
                  </section>
                )}
              </details>
            )}
            {techRow && (
              <TransitDialog row={techRow} copy={copy} language={language} onClose={() => setTechRow(null)} />
            )}
            {session && (
              <section className="birth-ask birth-section" aria-labelledby="daily-ask-title">
                <h2 id="daily-ask-title">{copy.askTitle}</h2>
                <div className="daily-chips">
                  {(copy.questions || []).map((item) => (
                    <button key={item} type="button" onClick={(event) => handleAsk(event, item)} disabled={asking}>
                      {item}
                    </button>
                  ))}
                </div>
                {answers.map((item, index) => (
                  <div className="birth-qa" key={index}>
                    <p className="birth-qa-question">{item.question}</p>
                    {item.answer && wrapReading(item.answer, language)}
                    {!item.answer && !item.error && <p className="birth-wait">{copy.looking}</p>}
                    {item.error && <p className="birth-error">{item.error}</p>}
                  </div>
                ))}
                <form className="birth-ask-form" onSubmit={handleAsk}>
                  <textarea
                    value={question}
                    rows={3}
                    maxLength={500}
                    aria-label={copy.askLabel}
                    placeholder={copy.askPlaceholder}
                    onChange={(event) => setQuestion(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) handleAsk(event);
                    }}
                  />
                  <button className="birth-draw" type="submit" disabled={asking || !question.trim()}>
                    {asking ? copy.asking : copy.ask}
                  </button>
                </form>
              </section>
            )}
            <section className="birth-section">
              <h2>{copy.keepGoing}</h2>
              <p className="daily-next">
                <Link to={chartHref('/birth-chart/', language)}>{copy.nextChart}</Link>
                <Link to={chartHref('/birth-chart-compatibility/', language)}>{copy.nextCompat}</Link>
                <Link to={CARD_OF_THE_DAY_PATHS[language] || '/card-of-the-day/'}>{copy.nextCard}</Link>
                <Link to={language === 'en' ? '/' : `/${language}/`}>{copy.nextTarot}</Link>
              </p>
            </section>
            <p className="birth-note">{copy.disclaimer}</p>
          </div>
        ) : (
          <>
            <header className="birth-intro">
              <p className="birth-eyebrow">{copy.eyebrow}</p>
              <h1>{copy.headingBefore} <em>{copy.headingEm}</em></h1>
              <p className="birth-lede">{copy.lede}</p>
            </header>
            <form className="birth-form daily-form" onSubmit={handleSubmit}>
              <BirthFields
                value={form}
                onChange={setForm}
                showName
                nameLabel={copy.fields.name}
                labels={copy.fields}
              />
              <PrivacyNote template={copy.cookieNote} label={copy.privacy} />
              {error && <p className="birth-error">{error}</p>}
              <button className="birth-draw" type="submit">{copy.submit}</button>
            </form>
            <section className="birth-about">
              <article className="birth-box">
                <h2>{copy.aboutTitle}</h2>
                <p>{copy.aboutBody}</p>
              </article>
              <div className="birth-points">
                {(copy.points || []).map((item, index) => (
                  <article key={item.title}>
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                ))}
              </div>
              <div className="birth-box">
                <h2>{copy.faqTitle}</h2>
                {(copy.faq || []).map((item) => (
                  <details key={item.q}>
                    <summary>{item.q}</summary>
                    <p>{item.a}</p>
                  </details>
                ))}
              </div>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default DailyHoroscope;
